import { useMutation, useQuery } from '@apollo/client';

import { UPDATE_NOTIFICATION_PREFERENCE } from '../graphql/mutations';
import { MY_NOTIFICATION_PREFERENCES_QUERY } from '../graphql/queries';
import type {
  NotificationPreference,
  NotificationType,
  UserError,
} from '../types/notifications.types';

interface UpdatePreferenceInput {
  type: NotificationType;
  inAppEnabled: boolean;
  emailEnabled: boolean;
}

/**
 * The signed-in user's notification preferences: the effective per-type list and the
 * single-type save. The mutation returns the saved preference so Apollo can reconcile, and we refetch
 * the list by operation name so the page reflects any server-side clamp (e.g. a mandatory type).
 */
export function useNotificationPreferences() {
  const { data, previousData, loading, error, refetch } = useQuery(
    MY_NOTIFICATION_PREFERENCES_QUERY,
    { fetchPolicy: 'cache-and-network' },
  );

  const [updateMutation, { loading: saving }] = useMutation(UPDATE_NOTIFICATION_PREFERENCE, {
    refetchQueries: ['MyNotificationPreferences'],
  });

  const preferences: NotificationPreference[] =
    (data ?? previousData)?.myNotificationPreferences ?? [];

  const update = async (input: UpdatePreferenceInput): Promise<UserError[]> => {
    const { data: result } = await updateMutation({ variables: { input } });
    return result?.updateNotificationPreference.errors ?? [];
  };

  return {
    preferences,
    loading: loading && !data && !previousData,
    error: error ?? null,
    refetch,
    update,
    saving,
  };
}
